import {
  Book20Regular,
  Money20Regular,
  BuildingGovernment20Regular,
  Calculator20Regular,
  Briefcase20Regular,
  Headset20Regular,
  ClipboardTaskListLtr20Regular,
  MoneySettings20Regular,
  Gavel20Regular,
  DocumentCheckmark20Regular,
  type FluentIcon,
} from '@fluentui/react-icons';
import type { SemanticColorKey } from './semanticColors';

/**
 * Staff role → visual definition for the {@link StaffBadges} chips (supervision
 * revamp Task 15). Keys mirror `StaffProfile.Roles` csv tokens exactly (see
 * `src/api/staffProfiles.ts` STAFF_ROLES). Each role maps to a Fluent icon, a
 * short label, and a `SemanticColorKey` — the actual light/dark color pair is
 * resolved at render time from the calibrated `semanticColorMaps` (see
 * `useSemanticColors`), so the chips are theme-aware and carry zero hardcoded
 * hex of their own.
 */
export interface RoleDef {
  /** Fluent icon component (rendered as `<Icon />`, inherits currentColor). */
  icon: FluentIcon;
  /** Short, plain-language label shown in headers / tooltips. */
  label: string;
  /** Semantic palette slot; resolves to a light + dark {fill,onFill} pair. */
  color: SemanticColorKey;
}

/** The canonical role tokens (kept in lockstep with the backend ValidRoles). */
export type StaffRole =
  | 'bookkeeper'
  | 'payroll-admin'
  | 'state-tax-admin'
  | 'income-tax-preparer'
  | 'business-services-coordinator'
  | 'receptionist'
  | 'bookkeeping-supervisor'
  | 'payroll-supervisor'
  | 'state-tax-supervisor'
  | 'income-tax-reviewer';

/**
 * Role → {icon, label, color}.
 *
 * The six "doer" roles each own a distinct semantic slot so the chips read apart
 * at a glance. The four oversight roles deliberately REUSE the colour of the
 * function they oversee (bookkeeping supervisor is brand, like bookkeeper) and
 * are told apart by their icon — that keeps each service line one colour family
 * instead of inventing four more palette slots. Every (colour, icon) pair is
 * still unique. Unknown role tokens intentionally have no entry — callers skip
 * them gracefully.
 */
export const roleDefs: Record<StaffRole, RoleDef> = {
  bookkeeper: { icon: Book20Regular, label: 'Bookkeeper', color: 'brand' },
  'payroll-admin': { icon: Money20Regular, label: 'Payroll Admin', color: 'success' },
  'state-tax-admin': {
    icon: BuildingGovernment20Regular,
    label: 'State Tax Admin',
    color: 'warning',
  },
  'income-tax-preparer': {
    icon: Calculator20Regular,
    label: 'Income Tax Preparer',
    color: 'danger',
  },
  'business-services-coordinator': {
    icon: Briefcase20Regular,
    label: 'Business Services Coordinator',
    color: 'purple',
  },
  receptionist: { icon: Headset20Regular, label: 'Receptionist', color: 'neutral' },
  'bookkeeping-supervisor': {
    icon: ClipboardTaskListLtr20Regular,
    label: 'Bookkeeping Supervisor',
    color: 'brand',
  },
  'payroll-supervisor': {
    icon: MoneySettings20Regular,
    label: 'Payroll Supervisor',
    color: 'success',
  },
  'state-tax-supervisor': {
    icon: Gavel20Regular,
    label: 'State Tax Supervisor',
    color: 'warning',
  },
  'income-tax-reviewer': {
    icon: DocumentCheckmark20Regular,
    label: 'Income Tax Reviewer',
    color: 'danger',
  },
};

/** Look up a role def, tolerating casing/whitespace; undefined for unknowns. */
export function getRoleDef(role: string): RoleDef | undefined {
  return roleDefs[role?.trim().toLowerCase() as StaffRole];
}
